import { KeyboardEvent, useCallback } from 'react';
import { FieldValues, UseFormSetValue } from 'react-hook-form';

interface UseAttachmentKeyboardProps<T extends FieldValues = any> {
  isFocus: boolean;
  debouncedValue: string;
  searchString: string;
  attachmentList: any[];
  relationsList: any[];
  searchFocusIndex: number;
  relationFocusIndex: number;
  registerName: string;
  setSearchFocusIndex: (value: number | ((prev: number) => number)) => void;
  setRelationFocusIndex: (value: number | ((prev: number) => number)) => void;
  setIsFocus: (value: boolean) => void;
  setValue: UseFormSetValue<T>;
  handleSelectAttachment: (attachment: any) => void;
  handleDeleteRelation: (id: string) => void;
}

export const useAttachmentKeyboard = <T extends FieldValues = any>({
  isFocus,
  debouncedValue,
  searchString,
  attachmentList,
  relationsList,
  searchFocusIndex,
  relationFocusIndex,
  registerName,
  setSearchFocusIndex,
  setRelationFocusIndex,
  setIsFocus,
  setValue,
  handleSelectAttachment,
  handleDeleteRelation,
}: UseAttachmentKeyboardProps<T>) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const isSearchListOpen = isFocus && attachmentList.length > 0;

      if (isSearchListOpen) {
        // SearchList navigation
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            setSearchFocusIndex((prev) =>
              prev < attachmentList.length - 1 ? prev + 1 : prev,
            );
            setRelationFocusIndex(-1);
            break;
          case 'ArrowUp':
            event.preventDefault();
            setSearchFocusIndex((prev) => (prev > 0 ? prev - 1 : 0));
            setRelationFocusIndex(-1);
            break;
          case 'Enter':
            event.preventDefault();
            if (searchFocusIndex >= 0 && attachmentList[searchFocusIndex]) {
              handleSelectAttachment(attachmentList[searchFocusIndex]);
            }
            break;
          case 'Escape':
            setSearchFocusIndex(-1);
            setIsFocus(false);
            setValue(registerName as any, '' as any);
            break;
        }
      }

      // DataItem navigation - always available
      switch (event.key) {
        case 'ArrowLeft':
          if (relationsList.length > 0) {
            event.preventDefault();
            setRelationFocusIndex((prev) => {
              if (prev === -1) return relationsList.length - 1;
              return prev > 0 ? prev - 1 : 0;
            });
            setSearchFocusIndex(-1);
          }
          break;
        case 'ArrowRight':
          if (relationsList.length > 0) {
            event.preventDefault();
            setRelationFocusIndex((prev) => {
              if (prev === -1) return 0;
              return prev < relationsList.length - 1 ? prev + 1 : prev;
            });
            setSearchFocusIndex(-1);
          }
          break;
        case 'Delete':
          if (relationFocusIndex >= 0 && relationsList[relationFocusIndex]) {
            event.preventDefault();
            handleDeleteRelation(relationsList[relationFocusIndex].id);
          }
          break;
        case 'Backspace':
          if (searchString.length === 0) {
            event.preventDefault();
            if (relationFocusIndex >= 0 && relationsList[relationFocusIndex]) {
              handleDeleteRelation(relationsList[relationFocusIndex].id);
            } else if (relationsList.length > 0) {
              handleDeleteRelation(relationsList[relationsList.length - 1].id);
            }
          }
          break;
        case 'Escape':
          setRelationFocusIndex(-1);
          setSearchFocusIndex(-1);
          break;
      }
    },
    [
      isFocus,
      debouncedValue,
      searchString,
      attachmentList,
      relationsList,
      searchFocusIndex,
      relationFocusIndex,
      registerName,
      setSearchFocusIndex,
      setRelationFocusIndex,
      setIsFocus,
      setValue,
      handleSelectAttachment,
      handleDeleteRelation,
    ],
  );

  return { handleKeyDown };
};
